<template>
  <div class="sci-page-wrapper" style="font-family: 'Inter', sans-serif; background: #f4f6f9; min-height: 100vh;">
    <div class="max-w-7xl mx-auto px-4 pt-24 pb-12">
      
      <!-- HEADER -->
      <div class="sci-page-header pb-8">
        <div class="flex items-center justify-between">
            <div>
                <h1 class="sci-page-title-only">Conferência</h1>
                <p class="text-sm text-slate-500 font-medium mt-1">Gestão e auditoria de abastecimentos</p>
            </div>
        </div>
      </div>

      <!-- ÁREA DE PESQUISA -->
      <section class="bg-white rounded-xl border border-slate-100 overflow-hidden shadow-sm mb-10">
          <div class="px-6 py-4 flex items-center gap-3" style="background-color: #333333;">
              <v-icon color="orange" small>mdi-filter-variant</v-icon>
              <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">Parâmetros de Auditoria</h2>
          </div>
          <div class="p-8">
              <div class="grid grid-cols-1 md:grid-cols-4 gap-6">
                  <div>
                    <label class="field-label">Data Inicial</label>
                    <InputDate v-model="filter.startDate" />
                  </div>
                  <div>
                    <label class="field-label">Data Final</label>
                    <InputDate v-model="filter.endDate" />
                  </div>
                  <div class="md:col-span-1">
                    <label class="field-label">Município</label>
                    <v-autocomplete
                      v-model="filter.prefecture_id"
                      :items="prefectures"
                      item-text="prefecture_social_name"
                      item-value="prefecture_id"
                      placeholder="SELECIONE..."
                      outlined dense hide-details
                      class="field-input"
                      clearable
                      @change="onPrefectureChange"
                      prepend-inner-icon="mdi-bank-outline"
                    />
                  </div>
                  <div v-if="userGroup == 1 || userData.table_key === 'prefectures'" class="md:col-span-1">
                    <label class="field-label">Fornecedor</label>
                    <v-autocomplete
                      v-model="filter.provider_id"
                      :items="providers"
                      item-text="provider_name"
                      item-value="provider_id"
                      placeholder="SELECIONE..."
                      outlined dense hide-details
                      class="field-input"
                      clearable
                      :disabled="!filter.prefecture_id"
                      @change="onProviderChange"
                      prepend-inner-icon="mdi-store-outline"
                    />
                  </div>
              </div>
              <div class="flex justify-end mt-8">
                  <button @click="handleFilter" class="bg-slate-800 hover:bg-slate-900 text-white rounded-xl shadow-lg flex items-center justify-center gap-3 px-10 h-14 transition-all transform hover:-translate-y-1 font-black text-[11px] uppercase tracking-widest">
                      <v-icon color="orange" size="18">mdi-magnify</v-icon>
                      Filtrar Registros
                  </button>
              </div>
          </div>
      </section>

      <!-- ÁREA DE RESULTADOS (TABS) -->
      <section class="bg-white rounded-xl border border-slate-100 overflow-hidden shadow-sm">
          <!-- TABS STRIP -->
          <div class="bg-slate-50 border-b border-slate-100 p-2 flex flex-wrap md:flex-nowrap gap-1">
             <button 
               v-for="t in filteredTabs" :key="t.index"
               @click="tab = t.index; handleTabChange(t.index)"
               :class="[
                 'px-6 py-4 text-[10px] font-black uppercase tracking-widest transition-all rounded-lg flex-1 flex items-center justify-center gap-3',
                 tab === t.index ? 'bg-white text-slate-800 shadow-sm border border-slate-200' : 'text-slate-400 hover:bg-slate-100 hover:text-slate-600'
               ]"
             >
               <v-icon size="16" :color="tab === t.index ? 'orange' : 'slate-300'">{{ t.icon }}</v-icon>
               <span class="hidden sm:inline">{{ t.label }}</span>
             </button>
          </div>

          <v-tabs-items v-model="tab" class="bg-white overflow-visible">
            <!-- CONFERÊNCIA -->
            <v-tab-item :key="0">
              <div class="p-0">
                <div class="px-8 py-4 border-b border-slate-100 flex justify-between items-center bg-white">
                    <div class="flex items-center gap-4 flex-1 max-w-md">
                      <v-text-field
                        v-model="searchCoupon"
                        placeholder="PESQUISAR POR CUPOM OU PLACA..."
                        solo flat dense hide-details
                        class="field-input-search"
                        prepend-inner-icon="mdi-magnify"
                      />
                    </div>
                </div>
                
                <v-data-table
                  :loading="loader"
                  :items="conferences"
                  :headers="conferenceHeaders"
                  :search="searchCoupon"
                  hide-default-footer
                  class="premium-table"
                  :page.sync="pageConferencia"
                  @page-count="pageCountConferencia = $event"
                  :items-per-page="itemsPerPage"
                >
                  <template v-slot:item.datehour="{ item }">
                    <span class="text-xs text-slate-500 font-bold font-mono">{{ convertDateTime(item.datehour) }}</span>
                  </template>
                  <template v-slot:item.model_desc="{ item }">
                    <div class="flex items-center gap-3" v-if="item.vehicle_id">
                       <div class="w-10 h-8 bg-slate-100 rounded border border-slate-200 flex items-center justify-center font-black text-[10px] text-slate-700">{{ item.vehicle_board }}</div>
                       <div class="flex flex-col">
                         <span class="text-[10px] text-slate-400 uppercase font-black tracking-widest">{{ item.brand_desc }} • {{ item.model_desc }}</span>
                       </div>
                    </div>
                    <span v-else class="text-[9px] text-orange-500 font-black uppercase tracking-[0.2em] bg-orange-50 px-3 py-1 rounded-full border border-orange-100">Correção Manual</span>
                  </template>
                  <template v-slot:item.supply_liters_stocked="{ item }">
                    <span class="font-black text-slate-700 text-xs">{{ item.supply_liters_stocked ? realMaskII(Number(item.supply_liters_stocked).toFixed(3)) : '0,000' }}</span>
                  </template>
                  <template v-slot:item.supply_bidding_price="{ item }">
                    <span class="font-black text-slate-700 text-xs">{{ item.supply_bidding_price ? realMaskII(Number(item.supply_bidding_price).toFixed(3)) : '0,000' }}</span>
                  </template>
                  <template v-slot:item.total_billing_price="{ item }">
                     <span class="font-black text-slate-400 text-xs tracking-tighter">R$ {{ (item.supply_bidding_price && item.supply_liters_stocked) ? realMask((Number(item.supply_bidding_price) * Number(item.supply_liters_stocked)).toFixed(2)) : '0,00' }}</span>
                  </template>
                  <template v-slot:item.status_chips="{ item }">
                    <div class="flex gap-1 flex-wrap">
                      <span v-if="item.supply_adjusted" class="px-2 py-0.5 rounded-md text-[8px] bg-blue-50 text-blue-600 font-black uppercase tracking-widest border border-blue-100">Adjusted</span>
                      <span v-if="item.supply_extra" class="px-2 py-0.5 rounded-md text-[8px] bg-rose-50 text-rose-600 font-black uppercase tracking-widest border border-rose-100">Bloqueado</span>
                      <span v-if="item.supply_confered" class="px-2 py-0.5 rounded-md text-[8px] bg-emerald-50 text-emerald-600 font-black uppercase tracking-widest border border-emerald-100">Conferido</span>
                      <span v-if="item.supply_billed" class="px-2 py-0.5 rounded-md text-[8px] bg-indigo-50 text-indigo-600 font-black uppercase tracking-widest border border-indigo-100">Faturado</span>
                    </div>
                  </template>
                  <template v-slot:item.supply_coupon="{ item }">
                    <span class="text-[10px] font-black text-slate-400 font-mono">#{{ item.supply_coupon }}</span>
                  </template>
                  <template v-slot:item.action="{ item }">
                    <div class="flex items-center justify-end gap-1 px-4">
                      <v-btn v-if="!item.supply_confered" icon small @click="getSupplyByUpdate(item.supply_id)" title="Editar" class="hover:bg-orange-50 group">
                        <v-icon size="16" color="orange" class="group-hover:scale-110 transition-transform">mdi-pencil-outline</v-icon>
                      </v-btn>
                      <v-btn v-if="!item.supply_confered" icon small @click="checkSupply(item.supply_id)" title="Conferir" class="hover:bg-emerald-50 group">
                        <v-icon size="16" color="emerald" class="group-hover:scale-110 transition-transform">mdi-check-bold</v-icon>
                      </v-btn>
                      <v-icon v-if="item.supply_confered" color="emerald" size="18">mdi-check-decagram</v-icon>
                    </div>
                  </template>
                </v-data-table>

                <!-- FOOTER COM TOTAIS -->
                <div class="bg-slate-50/50 px-8 py-8 border-t border-slate-100 grid grid-cols-1 md:grid-cols-3 gap-6">
                   <div class="bg-white p-6 rounded-xl border border-slate-100 shadow-sm flex items-center justify-between group">
                      <div>
                        <span class="text-[9px] font-black text-orange-400 uppercase tracking-widest block mb-1">TOTAL LICITADO</span>
                        <span class="text-2xl font-black text-orange-600 tracking-tight">R$ {{ totals.bidding_total ? realMask(Number(totals.bidding_total).toFixed(2)) : '0,00' }}</span>
                      </div>
                      <div class="p-3 bg-orange-50 rounded-lg group-hover:bg-orange-100 transition-colors">
                        <v-icon size="20" color="orange">mdi-currency-usd</v-icon>
                      </div>
                   </div>
                   
                   <div class="flex items-center justify-center">
                      <div class="text-center">
                        <span class="text-[10px] font-black text-slate-400 uppercase tracking-widest">Página Atual</span>
                        <div class="flex items-center gap-1 mt-2">
                           <v-btn icon small :disabled="pageConferencia === 1" @click="pageConferencia--"><v-icon size="18">mdi-chevron-left</v-icon></v-btn>
                           <div class="px-3 py-1 bg-white border border-slate-200 rounded text-[10px] font-black text-slate-800">{{ pageConferencia }} / {{ pageCountConferencia }}</div>
                           <v-btn icon small :disabled="pageConferencia === pageCountConferencia" @click="pageConferencia++"><v-icon size="18">mdi-chevron-right</v-icon></v-btn>
                        </div>
                      </div>
                   </div>
                </div>
              </div>
            </v-tab-item>

            <!-- FATURAMENTO -->
            <v-tab-item :key="1">
              <div class="p-0">
                <div class="px-8 py-6 border-b border-slate-100 flex items-center bg-white">
                    <div class="flex-1">
                        <h3 class="text-xs font-black text-slate-800 uppercase tracking-widest flex items-center gap-2">
                            <v-icon size="16" color="orange">mdi-file-document-multiple-outline</v-icon>
                            Listagem de Faturamento
                        </h3>
                        <p class="text-[10px] text-slate-400 font-bold uppercase mt-1">Gere relatórios consolidados por período e município</p>
                    </div>
                    <v-btn 
                      depressed 
                      style="background-color: #334155 !important;"
                      dark
                      class="rounded-xl px-8 h-12 text-[10px] font-black uppercase tracking-widest transition-all hover:scale-105 shadow-md" 
                      @click="printAllBillings"
                      :disabled="billings.length === 0"
                    >
                      <v-icon left size="18" color="orange">mdi-printer-settings</v-icon>
                      Relatório Geral
                    </v-btn>
                </div>
                <v-data-table
                  :loading="loader"
                  :items="billings"
                  :headers="billingHeaders"
                  hide-default-footer
                  class="premium-table"
                  :page.sync="pageFaturamento"
                  @page-count="pageCountFaturamento = $event"
                  :items-per-page="itemsPerPage"
                >
                  <template v-slot:item.bill_start_date="{ item }">
                    <div class="flex items-center gap-3">
                       <v-icon size="16" color="slate-300">mdi-calendar-range</v-icon>
                       <span class="text-xs text-slate-700 font-black uppercase tracking-tight">{{ convertDate(item.bill_start_date) }} <span class="text-slate-300 mx-1">→</span> {{ convertDate(item.bill_end_date) }}</span>
                    </div>
                  </template>
                  <template v-slot:item.sectors="{ item }">
                    <div class="flex flex-col py-3">
                       <span class="font-black text-slate-800 text-xs uppercase tracking-tight">{{ item.sec_name }}</span>
                       <span class="text-[9px] text-slate-400 font-bold uppercase tracking-widest mt-0.5">{{ item.subsec_desc || 'DEPARTAMENTO GERAL' }}</span>
                    </div>
                  </template>
                  <template v-slot:item.bill_interest="{ item }">
                    <span class="px-3 py-1 rounded-lg bg-slate-50 font-black text-[10px] text-slate-500 border border-slate-100">{{ item.bill_interest }} Docs</span>
                  </template>
                  <template v-slot:item.bill_total_value="{ item }">
                    <span class="font-black text-slate-400 text-xs">R$ {{ item.bill_total_value ? realMask(Number(item.bill_total_value).toFixed(2)) : '0,00' }}</span>
                  </template>
                  <template v-slot:item.bill_total_updated="{ item }">
                    <span class="text-orange-600 font-black text-sm tracking-tighter">R$ {{ item.bill_total_updated ? realMask(Number(item.bill_total_updated).toFixed(2)) : '0,00' }}</span>
                  </template>
                  <template v-slot:item.action="{ item }">
                    <div class="flex justify-end pr-4">
                       <v-btn icon small @click="printBillingReport(item)" class="hover:bg-slate-50 group">
                         <v-icon size="18" color="slate-400" class="group-hover:scale-110 transition-transform">mdi-printer-eye</v-icon>
                       </v-btn>
                    </div>
                  </template>
                </v-data-table>

                <!-- PAGINAÇÃO ESTILO 2ª IMAGEM -->
                <div class="bg-white border-t border-slate-100 px-8 py-5 flex flex-col items-center gap-4">
                   <span class="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                     Exibindo {{ paginationStartFaturamento }} de {{ billings.length }} registros
                   </span>
                   <div class="flex items-center gap-1">
                      <button
                        :disabled="pageFaturamento === 1"
                        @click="pageFaturamento--"
                        :class="['px-4 py-2 rounded-lg text-[11px] font-black uppercase tracking-widest transition-all', pageFaturamento === 1 ? 'text-slate-300 cursor-not-allowed' : 'text-slate-500 hover:bg-slate-100']"
                      >Anterior</button>
                       <button
                         v-for="p in visiblePagesFaturamento"
                         :key="p"
                         @click="pageFaturamento = p"
                         :class="['w-9 h-9 rounded-lg text-[11px] font-black transition-all', pageFaturamento === p ? 'bg-slate-800 text-white' : 'text-slate-500 hover:bg-slate-100']"
                       >{{ p }}</button>
                      <button
                        :disabled="pageFaturamento === pageCountFaturamento"
                        @click="pageFaturamento++"
                        :class="['px-4 py-2 rounded-lg text-[11px] font-black uppercase tracking-widest transition-all', pageFaturamento === pageCountFaturamento ? 'text-slate-300 cursor-not-allowed' : 'text-slate-500 hover:bg-slate-100']"
                      >Próximo</button>
                   </div>
                </div>

                <!-- TOTAL ACUMULADO -->
                <div class="bg-slate-800 px-10 py-10 flex items-center justify-between overflow-hidden relative rounded-b-xl">
                   <div class="absolute right-0 top-0 h-full w-64 bg-orange-500/5 skew-x-12 transform translate-x-24"></div>
                   <div class="relative z-10">
                      <span class="text-[10px] font-black text-slate-500 uppercase tracking-widest block mb-2">VALOR ACUMULADO NO PERÍODO</span>
                      <div class="flex items-baseline gap-2">
                        <span class="text-orange-500 font-black text-2xl">R$</span>
                        <span class="text-4xl font-black text-white italic tracking-tight">{{ totals.billing_price ? realMask(Number(totals.billing_price).toFixed(2)) : '0,00' }}</span>
                      </div>
                   </div>
                   <v-icon size="80" color="white" class="opacity-5 relative z-10">mdi-receipt-text-check-outline</v-icon>
                </div>
              </div>
            </v-tab-item>

            <!-- ADICIONAR ABASTECIMENTO -->
            <v-tab-item :key="2">
              <div class="p-10">
                 <v-form ref="form" v-model="valid" lazy-validation>
                    <div class="grid grid-cols-1 md:grid-cols-12 gap-6">
                       <!-- PRIMEIRA LINHA -->
                       <div class="md:col-span-3">
                          <label class="field-label">Data e Hora</label>
                          <v-text-field v-model="data.datehour" v-mask="'00/00/0000 00:00'" placeholder="DD/MM/AAAA HH:MM" outlined dense hide-details class="field-input font-mono" prepend-inner-icon="mdi-clock-outline" :rules="fieldRules" />
                       </div>
                       <div class="md:col-span-4">
                          <label class="field-label">Município de Origem</label>
                          <v-autocomplete v-model="data.prefecture_id" :items="prefectures" item-text="prefecture_social_name" item-value="prefecture_id" placeholder="SELECIONE A PREFEITURA..." outlined dense hide-details class="field-input" prepend-inner-icon="mdi-city-variant-outline" :rules="fieldRules" @change="onPrefectureManualChange(data.prefecture_id)" />
                       </div>
                       <div class="md:col-span-5">
                          <label class="field-label text-orange-600">Fornecedor / Posto</label>
                          <v-autocomplete v-model="data.provider_id" :items="providers" item-text="provider_name" item-value="provider_id" placeholder="SELECIONE O FORNECEDOR..." outlined dense hide-details class="field-input" prepend-inner-icon="mdi-store-outline" :rules="fieldRules" @change="getItems(data.prefecture_id, data.provider_id)" />
                       </div>

                       <!-- SEGUNDA LINHA -->
                       <div class="md:col-span-6">
                          <label class="field-label">Veículo da Frota</label>
                          <v-autocomplete v-model="data.vehicle_id" :items="vehicles" item-text="vehicle_board" item-value="vehicle_id" placeholder="SELECIONE O VEÍCULO..." outlined dense hide-details class="field-input" prepend-inner-icon="mdi-car-side" @change="onVehicleChange(data.vehicle_id)">
                            <template slot="selection" slot-scope="{ item }">{{ item.vehicle_board }} — {{ item.brand_desc }}</template>
                            <template slot="item" slot-scope="{ item }">
                               <div class="flex items-center gap-3 py-2">
                                  <div class="w-10 h-8 bg-slate-100 rounded border border-slate-200 flex items-center justify-center font-black text-[9px] text-slate-700">{{ item.vehicle_board }}</div>
                                  <div class="flex flex-col">
                                     <span class="text-[10px] text-slate-400 uppercase font-black tracking-widest">{{ item.brand_desc }} • {{ item.model_desc }}</span>
                                  </div>
                               </div>
                            </template>
                          </v-autocomplete>
                       </div>
                       <div class="md:col-span-6">
                          <label class="field-label">Condutor / Motorista</label>
                          <v-autocomplete v-model="data.driver_id" :items="drivers" item-text="driver_name" item-value="driver_id" placeholder="SELECIONE O MOTORISTA..." outlined dense hide-details class="field-input" prepend-inner-icon="mdi-account-outline" />
                       </div>

                       <!-- TERCEIRA LINHA -->
                       <div class="md:col-span-6">
                          <label class="field-label">Secretaria Municipal</label>
                          <v-text-field v-model="data.sec_name" placeholder="VINCULADA AO VEÍCULO..." readonly outlined dense hide-details class="field-input" prepend-inner-icon="mdi-briefcase-outline" />
                       </div>
                       <div class="md:col-span-6">
                          <label class="field-label">Sub-setor / Centro de Custo</label>
                          <v-text-field v-model="data.subsec_desc" placeholder="VINCULADO AO VEÍCULO..." readonly outlined dense hide-details class="field-input" prepend-inner-icon="mdi-office-building-outline" />
                       </div>

                       <!-- DADOS DO PRODUTO -->
                       <div class="md:col-span-8">
                          <label class="field-label text-orange-600">Ítem / Combustível Licitado</label>
                          <v-autocomplete v-model="data.item_id" :items="filteredItems" item-text="item_description_desc" item-value="item_id" placeholder="SELECIONE O ITEM..." outlined dense hide-details class="field-input" prepend-inner-icon="mdi-gas-station" :rules="fieldRules" @change="loadBidding(data.item_id)">
                            <template v-slot:selection="{ item }">
                              {{ item.item_description_desc }} <span class="text-slate-300 mx-2">—</span> R$ {{ item.item_price ? realMaskII(Number(item.item_price).toFixed(3)) : '0,00' }}
                            </template>
                          </v-autocomplete>
                       </div>
                       <div class="md:col-span-4">
                          <label class="field-label">Volume (Litros)</label>
                          <v-text-field v-model="data.supply_liters_stocked" v-mask-decimal.br="3" @change="calculateTotals('manual')" outlined dense hide-details class="field-input" prepend-inner-icon="mdi-opacity" :rules="fieldRules" />
                       </div>

                       <!-- RESUMO DE VALORES -->
                       <div class="md:col-span-12 grid grid-cols-1 md:grid-cols-4 gap-6">
                          <div class="bg-slate-900 rounded-xl p-6 border border-white/5 shadow-lg flex flex-col justify-center">
                             <label class="text-[9px] font-black text-slate-500 uppercase tracking-widest mb-2 block">Total do Cupom</label>
                             <div class="flex items-baseline gap-2">
                                <span class="text-orange-500 font-black text-sm">R$</span>
                                <span class="text-2xl font-black text-white tracking-tighter">{{ realMask(Number(data.supply_value || 0).toFixed(2)) }}</span>
                             </div>
                          </div>

                          <div class="bg-slate-50 rounded-xl p-6 border border-slate-100 flex flex-col justify-center">
                             <label class="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2 block">Total Licitado</label>
                             <div class="flex items-baseline gap-2">
                                <span class="text-slate-400 font-black text-sm">R$</span>
                                <span class="text-xl font-black text-slate-600 tracking-tighter">{{ realMask(Number(data.total_licitado || 0).toFixed(2)) }}</span>
                             </div>
                          </div>

                          <div class="flex flex-col justify-center">
                            <label class="field-label">Hodômetro (KM)</label>
                            <v-text-field v-model="data.supply_km" v-mask-number outlined dense hide-details class="field-input font-black" prepend-inner-icon="mdi-speedometer" placeholder="0" />
                          </div>

                          <div class="flex flex-col justify-center">
                             <label class="field-label">Nº Cupom / NF-e</label>
                             <v-text-field v-model="data.supply_coupon" outlined dense hide-details class="field-input font-mono" :rules="fieldRules" prepend-inner-icon="mdi-barcode-scan" />
                          </div>
                       </div>

                        <div class="md:col-span-12">
                           <label class="field-label">Justificativa da Correção</label>
                           <v-text-field v-model="data.justificativa" :rules="data.supply_extra ? fieldRules : []" placeholder="MOTIVO DO LANÇAMENTO MANUAL..." outlined dense hide-details="auto" class="field-input" prepend-inner-icon="mdi-comment-text-outline" />
                        </div>
                     </div>

                    <div class="mt-6 pt-5 border-t border-slate-100 flex items-center justify-between gap-4">
                       <div class="flex items-center gap-3 cursor-pointer" @click="data.supply_extra = !data.supply_extra">
                          <div :class="['w-8 h-8 rounded-lg flex items-center justify-center transition-all border-2', data.supply_extra ? 'bg-orange-500 border-orange-500' : 'bg-white border-slate-200']">
                             <v-icon :color="data.supply_extra ? 'white' : '#cbd5e1'" size="15">mdi-check-bold</v-icon>
                          </div>
                          <div>
                            <span class="text-[10px] font-black text-slate-700 uppercase tracking-widest block">Lançamento de Correção</span>
                            <span class="text-[9px] text-slate-400 uppercase tracking-widest">Auditado separadamente</span>
                          </div>
                       </div>
                       <v-btn @click="validate" color="#333333" dark depressed class="rounded-xl px-10 h-10 text-[11px] font-black uppercase tracking-widest"><v-icon left color="orange" size="18">mdi-shield-check-outline</v-icon>Salvar Abastecimento</v-btn>
                    </div>
                 </v-form>
              </div>
            </v-tab-item>

            <!-- ABASTECIMENTOS BLOQUEADOS (NOVO) -->
            <v-tab-item :key="3">
              <div class="p-10">
                  <v-form ref="blockedForm" v-model="valid" lazy-validation>
                    <div class="grid grid-cols-1 md:grid-cols-12 gap-6">
                       <!-- PRIMEIRA LINHA -->
                       <div class="md:col-span-3">
                          <label class="field-label">Data e Hora</label>
                          <v-text-field v-model="dataBlocked.datehour" v-mask="'00/00/0000 00:00'" placeholder="DD/MM/AAAA HH:MM" outlined dense hide-details class="field-input font-mono" prepend-inner-icon="mdi-clock-outline" :rules="fieldRules" />
                       </div>
                       <div class="md:col-span-4">
                          <label class="field-label">Município de Origem</label>
                          <v-autocomplete v-model="dataBlocked.prefecture_id" :items="prefectures" item-text="prefecture_social_name" item-value="prefecture_id" placeholder="SELECIONE A PREFEITURA..." outlined dense hide-details class="field-input" prepend-inner-icon="mdi-city-variant-outline" :rules="fieldRules" @change="onPrefectureManualChange(dataBlocked.prefecture_id, 'blocked')" />
                       </div>
                       <div class="md:col-span-5">
                          <label class="field-label text-orange-600">Fornecedor / Posto</label>
                          <v-autocomplete v-model="dataBlocked.provider_id" :items="providers" item-text="provider_name" item-value="provider_id" placeholder="SELECIONE O FORNECEDOR..." outlined dense hide-details class="field-input" prepend-inner-icon="mdi-store-outline" :rules="fieldRules" @change="getItems(dataBlocked.prefecture_id, dataBlocked.provider_id, 'blocked')" />
                       </div>

                       <!-- DADOS DO PRODUTO (SEM VEÍCULO E MOTORISTA) -->
                       <div class="md:col-span-8">
                          <label class="field-label text-orange-600">Ítem / Combustível Licitado</label>
                          <v-autocomplete v-model="dataBlocked.item_id" :items="filteredItems" item-text="item_description_desc" item-value="item_id" placeholder="SELECIONE O ITEM..." outlined dense hide-details class="field-input" prepend-inner-icon="mdi-gas-station" :rules="fieldRules" @change="loadBidding(dataBlocked.item_id, 'blocked')">
                            <template v-slot:selection="{ item }">
                              {{ item.item_description_desc }} <span class="text-slate-300 mx-2">—</span> R$ {{ item.item_price ? realMaskII(Number(item.item_price).toFixed(3)) : '0,00' }}
                            </template>
                          </v-autocomplete>
                       </div>
                       <div class="md:col-span-4">
                          <label class="field-label">Volume (Litros)</label>
                          <v-text-field v-model="dataBlocked.supply_liters_stocked" v-mask-decimal.br="3" @change="calculateTotals('blocked')" outlined dense hide-details class="field-input" prepend-inner-icon="mdi-opacity" :rules="fieldRules" />
                       </div>

                       <!-- RESUMO DE VALORES -->
                       <div class="md:col-span-12 grid grid-cols-1 md:grid-cols-3 gap-6">
                          <div class="bg-slate-900 rounded-xl p-6 border border-white/5 shadow-lg flex flex-col justify-center">
                             <label class="text-[9px] font-black text-slate-500 uppercase tracking-widest mb-2 block">Total do Cupom</label>
                             <div class="flex items-baseline gap-2">
                                <span class="text-orange-500 font-black text-sm">R$</span>
                                <span class="text-2xl font-black text-white tracking-tighter">{{ realMask(Number(dataBlocked.supply_value || 0).toFixed(2)) }}</span>
                             </div>
                          </div>

                          <div class="bg-slate-50 rounded-xl p-6 border border-slate-100 flex flex-col justify-center">
                             <label class="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2 block">Total Licitado</label>
                             <div class="flex items-baseline gap-2">
                                <span class="text-slate-400 font-black text-sm">R$</span>
                                <span class="text-xl font-black text-slate-600 tracking-tighter">{{ realMask(Number(dataBlocked.total_licitado || 0).toFixed(2)) }}</span>
                             </div>
                          </div>



                          <div class="flex flex-col justify-center">
                            <label class="field-label">Nº Cupom / NF-e</label>
                            <v-text-field v-model="dataBlocked.supply_coupon" outlined dense hide-details class="field-input font-mono" :rules="fieldRules" prepend-inner-icon="mdi-barcode-scan" />
                          </div>
                       </div>
                    </div>

                    <div class="mt-6 pt-5 border-t border-slate-100 text-right">
                       <v-btn 
                         @click="validateBlocked" 
                         color="#333333"
                         dark
                         depressed
                         class="rounded-xl px-10 h-10 text-[11px] font-black uppercase tracking-widest"
                       >
                          <v-icon left color="orange" size="18">mdi-lock-outline</v-icon>
                          Salvar Bloqueado
                       </v-btn>
                    </div>
                 </v-form>
              </div>
            </v-tab-item>

            <!-- ABASTECIMENTOS EXTRAS / CORREÇÕES -->
            <v-tab-item :key="4">
              <div class="p-0">
                <v-data-table
                  :headers="abastecimentoheaders"
                  :items="abastecimentoData"
                  hide-default-footer
                  :loading="loader"
                  class="premium-table"
                  :page.sync="pageAbast2"
                  @page-count="pageCountAbast2 = $event"
                  :items-per-page="itemsPerPage"
                >
                  <template v-slot:item.datehour="{ item }">
                    <span class="text-xs text-slate-500 font-bold font-mono">{{ convertDateTime(item.datehour) }}</span>
                  </template>
                  <template v-slot:item.model_desc="{ item }">
                    <div class="flex items-center gap-3" v-if="item.vehicle_id">
                       <div class="w-10 h-8 bg-slate-100 rounded border border-slate-200 flex items-center justify-center font-black text-[9px] text-slate-700">{{ item.vehicle_board }}</div>
                       <div class="flex flex-col">
                         <span class="text-[9px] text-slate-400 font-extrabold uppercase tracking-widest">{{ item.brand_desc }} • {{ item.model_desc }}</span>
                       </div>
                    </div>
                    <span v-else class="text-[9px] text-slate-300 font-black uppercase tracking-[0.2em] italic px-3 py-1 bg-slate-50 rounded-full border border-slate-100">Não Vinculado</span>
                  </template>
                  <template v-slot:item.driver_name="{ item }">
                    <span class="text-[10px] font-black text-slate-600 uppercase tracking-tight">{{ item.driver_name || 'NÃO INFORMADO' }}</span>
                  </template>
                  <template v-slot:item.supply_liters_stocked="{ item }">
                    <span class="font-black text-slate-700 text-xs">{{ item.supply_liters_stocked ? realMaskII(Number(item.supply_liters_stocked).toFixed(3)) : "0.000" }}</span>
                  </template>
                  <template v-slot:item.supply_status="{ item }">
                    <div class="flex items-center gap-2">
                       <div class="w-1.5 h-1.5 rounded-full bg-emerald-500"></div>
                       <span class="text-[9px] font-black uppercase tracking-widest text-emerald-600">{{ item.supply_status }}</span>
                    </div>
                  </template>
                </v-data-table>

                <div class="bg-slate-50/50 px-8 py-8 border-t border-slate-100 flex items-center justify-between">
                    <span class="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                      Mostrando <span class="text-slate-900">{{ paginationStartAbast2 }}</span> - <span class="text-slate-900">{{ Math.min(pageAbast2 * itemsPerPage, abastecimentoData.length) }}</span> de <span class="text-slate-900">{{ abastecimentoData.length }}</span> registros
                    </span>
                    
                    <div class="flex items-center gap-1">
                       <v-btn icon small :disabled="pageAbast2 === 1" @click="pageAbast2--"><v-icon size="18">mdi-chevron-left</v-icon></v-btn>
                       <div class="px-3 py-1 bg-white border border-slate-200 rounded text-[10px] font-black text-slate-800">{{ pageAbast2 }} / {{ pageCountAbast2 }}</div>
                       <v-btn icon small :disabled="pageAbast2 === pageCountAbast2" @click="pageAbast2++"><v-icon size="18">mdi-chevron-right</v-icon></v-btn>
                    </div>
                </div>
              </div>
            </v-tab-item>
          </v-tabs-items>
      </section>
    </div>

    <!-- DIALOG EDIT -->
    <v-dialog v-model="showForm" width="600" persistent>
      <v-card class="rounded-[2rem] overflow-hidden border border-slate-200 shadow-2xl">
        <div class="px-10 py-6 flex items-center justify-between" style="background-color: #333333;">
           <div class="flex items-center gap-4">
              <div class="p-2 bg-orange-500/10 rounded-lg">
                <v-icon color="orange" small>mdi-pencil-box-outline</v-icon>
              </div>
              <h3 class="text-[11px] font-black text-white uppercase tracking-[0.2em]">Alterar Lançamento</h3>
           </div>
           <button @click="showForm = false" class="text-white hover:text-orange-400 transition-colors bg-white/10 p-2 rounded-xl">
             <v-icon size="20" color="currentColor">mdi-close</v-icon>
           </button>
        </div>
        <v-card-text class="py-10 px-10">
          <v-form ref="editForm">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div class="md:col-span-2">
                <label class="field-label">Data e Hora do Registro</label>
                <v-text-field v-model="dataAdjust.datehour" v-mask="'00/00/0000 00:00'" outlined dense hide-details class="field-input font-mono" prepend-inner-icon="mdi-clock-outline" />
              </div>
              <div>
                 <label class="field-label">Quantidade (Litros)</label>
                 <v-text-field v-model="dataAdjust.supply_liters_stocked" v-mask-decimal.br="3" @change="calculateSubTotals" outlined dense hide-details class="field-input" />
              </div>
              <div class="md:col-span-2">
                <div class="bg-slate-900 p-6 rounded-xl border-l-4 border-orange-500 shadow-lg flex items-center justify-between group">
                   <div>
                     <label class="text-[9px] font-black text-slate-500 uppercase tracking-widest mb-1 block">Novo Valor Calculado</label>
                     <div class="flex items-baseline gap-2">
                        <span class="text-orange-500 font-black text-lg">R$</span>
                        <span class="text-3xl font-black text-white tracking-tight">{{ realMask(Number(dataAdjust.supply_value || 0).toFixed(2)) }}</span>
                     </div>
                   </div>
                   <v-icon size="40" color="white" class="opacity-10">mdi-calculator-variant-outline</v-icon>
                </div>
              </div>
              <div class="md:col-span-2">
                <label class="field-label">Nº Documento Fiscal / Cupom</label>
                <v-text-field v-model="dataAdjust.supply_coupon" outlined dense hide-details class="field-input font-mono" prepend-inner-icon="mdi-barcode-scan" />
              </div>
            </div>
          </v-form>
        </v-card-text>
        <div class="px-10 py-8 bg-slate-50 flex flex-col md:flex-row justify-end gap-4 border-t border-slate-100">
           <button @click="showForm = false" class="px-8 py-4 text-[11px] font-black text-slate-400 hover:text-slate-600 transition-colors uppercase tracking-[0.2em]">Cancelar</button>
           <button @click="ajust" :disabled="loader" class="bg-slate-800 hover:bg-slate-950 text-white font-black text-[11px] uppercase tracking-[0.2em] px-12 h-16 rounded-2xl shadow-2xl flex items-center justify-center gap-3 group transition-all transform hover:-translate-y-1">
              <v-icon v-if="!loader" color="orange" size="20">mdi-check-all</v-icon>
              <v-icon v-else small class="animate-spin" color="orange">mdi-loading</v-icon>
              <span>Efetivar Alterações</span>
           </button>
        </div>
      </v-card>
    </v-dialog>

    <!-- NOTIFICAÇÕES -->
    <v-snackbar
      v-model="snack.show"
      :color="snack.color"
      timeout="4000"
      top
      elevation="24"
      class="custom-snack"
    >
      <div class="flex items-center gap-3">
        <v-icon color="white" small>{{ snack.color === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle' }}</v-icon>
        <span class="text-xs font-black uppercase tracking-widest">{{ snack.message }}</span>
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn dark icon v-bind="attrs" @click="snack.show = false">
          <v-icon size="16">mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import Helpers from "@/mixins/helpers.js";
import InputDate from "@/components/InputDate.vue";

export default {
  props: ["id"],
  mixins: [Helpers],
  components: { InputDate },
  data: () => ({
    userGroup: null,
    showForm: false,
    tab: 0,
    loader: false,
    valid: false,
    tabsLabels: ["Conferência", "Faturamento", "Abastecimento Manual", "Abastecimentos Bloqueados", "Abastecimentos 2"],
    tabsIcons: ["mdi-calendar-check-outline", "mdi-receipt-text-outline", "mdi-plus-circle-outline", "mdi-lock-outline", "mdi-shield-alert-outline"],
    
    itemsPerPage: 10,
    pageConferencia: 1,
    pageCountConferencia: 0,
    pageFaturamento: 1,
    pageCountFaturamento: 0,
    pageAbast2: 1,
    pageCountAbast2: 0,

    dataAdjust: {
      supply_id: null,
      supply_liters_stocked: 0,
      supply_price_day: 0,
      supply_coupon: null,
      supply_value: 0,
    },
    totals: {
      bidding_total: 0,
      day_value: 0,
      billing_price: 0,
    },
    subsectors: [],
    secretariats: [],
    drivers: [],
    providers: [],
    prefectures: [],
    vehicles: [],
    items: [],
    filteredItems: [],
    conferences: [],
    billings: [],
    abastecimentoData: [],
    searchCoupon: "",
    
    filter: {
      endDate: "",
      startDate: "",
      provider_id: null,
      prefecture_id: null,
    },
    
    data: {
      supply_id: null,
      supply_price_day: 0,
      datehour: "",
      supply_bidding_price: 0,
      supply_adjusted: false,
      supply_liters_stocked: 0,
      supply_billed: false,
      supply_extra: false,
      supply_extra_type: 3,
      supply_status: "CONCLUIDO",
      item_id: null,
      vehicle_id: null,
      driver_id: null,
      sec_id: null,
      subsec_id: null,
      sec_name: "",
      subsec_desc: "",
      justificativa: null,
      supply_km: "",
      supply_coupon: "",
      total_licitado: 0,
      supply_bidding_value: 0,
      provider_id: null,
      prefecture_id: null,
    },
    dataBlocked: {
      supply_id: null,
      supply_price_day: 0,
      datehour: "",
      supply_bidding_price: 0,
      supply_adjusted: false,
      supply_liters_stocked: 0,
      supply_billed: false,
      supply_extra: true,
      supply_extra_type: 1,
      supply_status: "BLOQUEADO PARA DISTRIBUIÇÃO",
      item_id: null,
      vehicle_id: null,
      driver_id: null,
      sec_id: null,
      subsec_id: null,
      sec_name: "",
      subsec_desc: "",
      justificativa: null,
      supply_km: "",
      supply_coupon: "",
      total_licitado: 0,
      supply_bidding_value: 0,
      provider_id: null,
      prefecture_id: null,
    },

    fieldRules: [(v) => !!v || "Este campo é requerido"],
    snack: { show: false, message: "", color: "success" },

    conferenceHeaders: [
      { text: "Data/Hora", value: "datehour", align: 'start' },
      { text: "Veículo / Identificação", value: "model_desc" },
      { text: "Produto", value: "item_description_desc" },
      { text: "Litros", value: "supply_liters_stocked", align: 'center' },
      { text: "Preço Licitado", value: "supply_bidding_price", align: 'center' },
      { text: "T. Licitado", value: "total_billing_price", align: 'center' },
      { text: "Status", value: "status_chips", sortable: false },
      { text: "Cupom", value: "supply_coupon", align: 'center' },
      { text: "Ações", align: "right", value: "action", sortable: false },
    ],
    billingHeaders: [
      { text: "Período de Referência", value: "bill_start_date" },
      { text: "Secretaria / Unidade", value: "sectors" },
      { text: "Docs", value: "bill_interest", align: 'center' },
      { text: "Total Licitado", value: "bill_total_value", align: 'center' },
      { text: "Total Faturado", value: "bill_total_updated", align: 'center' },
      { text: "Auditoria", align: "right", value: "action", sortable: false },
    ],
    abastecimentoheaders: [
      { text: "Data e Hora", value: "datehour", align: "start" },
      { text: "Veículo", value: "model_desc" },
      { text: "Condutor", value: "driver_name" },
      { text: "Item", value: "item_description_desc" },
      { text: "Litros", value: "supply_liters_stocked", align: "center" },
      { text: "Status", value: "supply_status", align: "center" },
    ],
  }),

  computed: {
    paginationStartConferencia() { return this.conferences.length > 0 ? (this.pageConferencia - 1) * this.itemsPerPage + 1 : 0; },
    visiblePagesConferencia() {
      let pages = []; const totalVisible = 5;
      let start = Math.max(1, this.pageConferencia - Math.floor(totalVisible / 2));
      let end = Math.min(this.pageCountConferencia, start + totalVisible - 1);
      if (end - start + 1 < totalVisible) start = Math.max(1, end - totalVisible + 1);
      for (let i = start; i <= end; i++) pages.push(i);
      return pages;
    },
    paginationStartFaturamento() { return this.billings.length > 0 ? (this.pageFaturamento - 1) * this.itemsPerPage + 1 : 0; },
    visiblePagesFaturamento() {
      let pages = []; const totalVisible = 5;
      let start = Math.max(1, this.pageFaturamento - Math.floor(totalVisible / 2));
      let end = Math.min(this.pageCountFaturamento, start + totalVisible - 1);
      if (end - start + 1 < totalVisible) start = Math.max(1, end - totalVisible + 1);
      for (let i = start; i <= end; i++) pages.push(i);
      return pages;
    },
    paginationStartAbast2() { return this.abastecimentoData.length > 0 ? (this.pageAbast2 - 1) * this.itemsPerPage + 1 : 0; },
    visiblePagesAbast2() {
      let pages = []; const totalVisible = 5;
      let start = Math.max(1, this.pageAbast2 - Math.floor(totalVisible / 2));
      let end = Math.min(this.pageCountAbast2, start + totalVisible - 1);
      if (end - start + 1 < totalVisible) start = Math.max(1, end - totalVisible + 1);
      for (let i = start; i <= end; i++) pages.push(i);
      return pages;
    },
    userData() {
      return this.$store.getters.getUserData;
    },
    filteredTabs() {
      const all = [
        { label: "Conferência", icon: "mdi-calendar-check-outline", index: 0 },
        { label: "Faturamento", icon: "mdi-receipt-text-outline", index: 1 },
        { label: "Abastecimento Manual", icon: "mdi-plus-circle-outline", index: 2 },
        { label: "Abastecimentos Bloqueados", icon: "mdi-lock-outline", index: 3 },
        { label: "Abastecimentos 2", icon: "mdi-shield-alert-outline", index: 4 }
      ];

      // Se for do grupo de fornecedores (Identificado estritamente pelo table_key)
      if (this.userData.table_key === 'providers') {
        return all.filter(t => [3, 4].includes(t.index));
      }
      return all;
    },
  },

  created() {
    this.Helpers;
    const userData = this.$store.getters.getUserData;
    this.userGroup = userData.group_id;

    if (this.userGroup == 1) {
       this.$http.get("/prefectures").then((r) => (this.prefectures = r.data));
    } else if (userData.table_key === 'prefectures') {
       this.filter.prefecture_id = userData.table_id;
       this.$http.get("/prefectures").then((r) => {
         this.prefectures = r.data;
         this.getProviders();
       });
       this.tab = 0;
       this.search();
    } else {
       this.filter.provider_id = userData.table_id;
       this.$http.get(`/supplies/prefectures/conference/${userData.table_id}`).then((r) => (this.prefectures = r.data));
       
       // Se for fornecedor, inicia na aba de Bloqueados (index 3)
       if (userData.table_key === 'providers') {
         this.tab = 3;
         this.searchAbastecimento();
       } else {
         this.tab = 0;
         this.search();
       }
    }
    if (this.id) {
      this.loadSingleSupply(this.id);
    } else {
      this.data.datehour = this.getCurrentDateTimeBr();
      this.dataBlocked.datehour = this.getCurrentDateTimeBr();
    }
  },

  methods: {
    onPrefectureChange(val) {
      if (this.userGroup == 1) { this.filter.prefecture_id = val; this.getProviders(); }
    },
    getProviders() {
      if (!this.filter.prefecture_id) { this.providers = []; return; }
      this.$http.get(`/providers/prefecture/${this.filter.prefecture_id}`).then((r) => {
          this.providers = r.data;
          if (this.providers.length === 0) this.$http.get(`/readjust/providers/${this.filter.prefecture_id}`).then(r2 => this.providers = r2.data);
      }).catch((e) => this.showSnack("Erro: " + (e.response ? e.response.data : e.message), "error"));
    },
    handleFilter() {
      if (this.tab === 0) this.search();
      if (this.tab === 1) this.searchBillings();
      if (this.tab === 4) this.searchAbastecimento();
    },
    handleTabChange(i) {
      if (i === 0) this.search();
      if (i === 1) this.searchBillings();
      if (i === 4) this.searchAbastecimento();
    },
    loadSingleSupply(id) {
       this.$store.dispatch("setLoader", true);
       this.$http.get(`supplies/single/${id}`).then((r) => {
         this.data = r.data[0];
         this.$http.get(`/supplies/providers/${r.data[0].provider_id}`).then((i) => this.items = i.data);
         this.$store.dispatch("setLoader", false);
       });
    },
    search() {
      if (!this.filter.prefecture_id || (this.userGroup == 1 && !this.filter.provider_id)) { this.showSnack("Selecione o município e fornecedor", "warning"); return; }
      this.totals.day_value = 0; this.totals.bidding_total = 0; this.loader = true;
      this.$http.post("/supplies/repairvalues").then(() => {
        this.$http.post("/supplies/conferences", this.filter).then((r) => {
            this.conferences = r.data.filter(s => s.supply_extra_type != 1 || s.supply_status == 'CONCLUIDO');
            this.conferences.forEach((s) => {
              const l = s.supply_liters_stocked || 0;
              this.totals.bidding_total += parseFloat(s.supply_bidding_price || 0) * parseFloat(l);
            });
            this.loader = false;
          }).catch((e) => { this.showSnack(e.message, "error"); this.loader = false; });
      });
    },
    searchBillings() {
      if (!this.filter.prefecture_id || (this.userGroup == 1 && !this.filter.provider_id)) return;
      this.totals.billing_price = 0; this.loader = true;
      this.$http.post(`/billings/provider`, this.filter).then((r) => {
          this.billings = r.data;
          this.billings.forEach((b) => this.totals.billing_price += parseFloat(b.bill_total_value));
          this.loader = false;
        }).catch((e) => { this.showSnack(e.message, "error"); this.loader = false; });
    },
    searchAbastecimento() {
      if (!this.filter.prefecture_id || (this.userGroup == 1 && !this.filter.provider_id)) return;
      this.loader = true;
      this.$http.put("/supplies/billings", { ...this.filter, all_status: true, supply_extra: true, supply_extra_type: 1, is_autorized: true, allow_vehicle_null: true })
        .then((r) => { this.abastecimentoData = r.data; this.loader = false; })
        .catch((e) => { this.showSnack(e.message, "error"); this.loader = false; });
    },
    getVehicles(pid) { if (!pid) return; this.$http.get(`/vehicles/prefectures/${pid}`).then((r) => this.vehicles = r.data.filter(v => v.vehicle_active)); },
    getDrivers(pid) { if (!pid) return; this.$http.get(`/supplies/drivers/${pid}`).then((r) => this.drivers = r.data); },
    getSecretariats(pid) { if (!pid) return; this.$http.get(`/prefectures/secretariats/${pid}`).then((r) => this.secretariats = r.data); },
    getSubsectors(sid) { if (!sid) return; this.$http.get(`/prefectures/subsectors/${sid}`).then((r) => this.subsectors = r.data); },
    getItems(pid, prIdInput = null, target = 'manual') {
      if (!pid) return; 
      const prId = prIdInput || (this.userGroup == 1 ? this.filter.provider_id : this.$store.getters.getUserData.table_id);
      if (!prId) return; 
      this.$http.get(`/biddings/items/${pid}/${prId}`).then((i) => {
        this.items = i.data;
        const targetData = target === 'manual' ? this.data : this.dataBlocked;
        if (targetData.vehicle_id) {
          this.onVehicleChange(targetData.vehicle_id);
        } else {
          this.filteredItems = i.data;
        }
      });
    },
    onPrefectureManualChange(pid, target = 'manual') {
       const targetData = target === 'manual' ? this.data : this.dataBlocked;
       this.getVehicles(pid);
       this.getSecretariats(pid);
       this.getDrivers(pid);
       this.$http.get(`/readjust/providers/${pid}`).then(r => {
          this.providers = r.data;
          if (targetData.provider_id) {
             this.getItems(pid, targetData.provider_id, target);
          }
       });
    },
    onProviderChange() {
      if (this.data.prefecture_id) {
        this.getItems(this.data.prefecture_id);
      }
    },
    onVehicleChange(vid) {
      const vehicle = this.vehicles.find(v => v.vehicle_id == vid);
      if (vehicle) {
        this.data.sec_id = vehicle.sec_id;
        this.data.subsec_id = vehicle.subsec_id;
        this.data.sec_name = vehicle.sec_name || "";
        this.data.subsec_desc = vehicle.subsec_desc || "";
        
        if (this.items && this.items.length > 0) {
          const vFuel = String(vehicle.fuel_desc || "").toUpperCase();
          const isFlex = vFuel.includes("FLEX") || vFuel.includes("ALCOOL") || (vFuel.includes("GASOLINA") && (vFuel.includes("ETANOL") || vFuel.includes("ALCOOL")));
          
          let filtered = [];
          if (isFlex) {
            // Para veículos Flex, aceita Gasolina, Etanol, Álcool ou Flex
            filtered = this.items.filter(i => {
              const iDesc = String(i.item_description_desc || "").toUpperCase();
              const iFuel = String(i.fuel_desc || "").toUpperCase();
              return iDesc.includes("GASOLINA") || iDesc.includes("ETANOL") || iDesc.includes("ALCOOL") || iDesc.includes("FLEX") ||
                     iFuel.includes("GASOLINA") || iFuel.includes("ETANOL") || iFuel.includes("ALCOOL") || iFuel.includes("FLEX") ||
                     String(i.fuel_id) == String(vehicle.fuel_id);
            });
          } else {
            // Match direto por ID para outros tipos (Diesel, etc)
            filtered = this.items.filter(i => String(i.fuel_id) == String(vehicle.fuel_id));
          }
          
          this.filteredItems = filtered.length > 0 ? filtered : this.items;
        } else {
          this.filteredItems = this.items;
        }
      } else {
        this.data.sec_id = null;
        this.data.subsec_id = null;
        this.data.sec_name = "";
        this.data.subsec_desc = "";
        this.filteredItems = this.items;
      }
    },
    loadBidding(id, target = 'manual') {
      const item = this.items.find((v) => v.item_id == id);
      const targetData = target === 'manual' ? this.data : this.dataBlocked;
      if (item) { 
        const biddingPrice = item.item_billing_price || item.item_price || 0;
        const marketPrice = item.item_price || item.item_billing_price || 0;
        
        targetData.supply_bidding_price = Number(biddingPrice).toFixed(3); 
        targetData.supply_price_day = Number(marketPrice).toFixed(3);
        
        this.calculateTotals(target); 
      }
    },
    calculateTotals(target = 'manual') {
      const targetData = target === 'manual' ? this.data : this.dataBlocked;
      const l = this.convertDecimalNumberII(targetData.supply_liters_stocked || 0);
      const bp = this.convertDecimalNumberII(targetData.supply_bidding_price || 0);
      const mp = this.convertDecimalNumberII(targetData.supply_price_day || 0);

      targetData.supply_value = Number(mp * l).toFixed(2);
      targetData.total_licitado = Number(bp * l).toFixed(2);
      targetData.supply_bidding_value = Number(bp * l).toFixed(2);
    },
    calculateSubTotals() {
      const p = this.convertDecimalNumberII(this.dataAdjust.supply_price_day || 0);
      const l = this.convertDecimalNumberII(this.dataAdjust.supply_liters_stocked || 0);
      this.dataAdjust.supply_value = Number(p * l).toFixed(2);
    },
    checkSupply(id) {
      if (!confirm("Confirmar o abastecimento?")) return;
      this.$store.dispatch("setLoader", true);
      this.$http.put(`/supplies/single/${id}`, { supply_id: id, supply_confered: true, supply_status: "CONCLUIDO", update_simples: true }).then(() => {
          this.showSnack("Conferido!", "success"); this.$store.dispatch("setLoader", false); this.search();
        }).catch((e) => { this.showSnack(e.message, "error"); this.$store.dispatch("setLoader", false); });
    },
    getSupplyByUpdate(id) {
      this.$http.get(`/supplies/single/${id}`).then((r) => {
        this.dataAdjust = r.data[0];
        this.dataAdjust.supply_liters_stocked = Number(r.data[0].supply_liters_stocked || 0).toFixed(3);
        this.dataAdjust.supply_price_day = Number(r.data[0].supply_price_day || 0).toFixed(3);
        this.dataAdjust.datehour = this.convertDateTime(r.data[0].datehour);
        this.showForm = true;
      }).catch(() => this.showSnack("Erro ao carregar dados", "error"));
    },
    ajust() {
      this.loader = true; this.dataAdjust.supply_adjusted = true; this.dataAdjust.supply_status = "CONCLUIDO";
      this.$http.put(`/supplies/single/${this.dataAdjust.supply_id}`, this.dataAdjust).then((r) => {
          this.showSnack(r.data.result || "Ajustado!", "success");
          setTimeout(() => { this.showForm = false; this.loader = false; this.search(); }, 1500);
        }).catch((e) => { this.showSnack(e.message, "error"); this.loader = false; });
    },
    validate() {
      if (!this.$refs.form.validate()) { this.showSnack("Preencha os obrigatórios", "warning"); return; }
      if (!this.data.vehicle_id && !this.data.supply_extra) { this.showSnack("Marque como correção!", "warning"); return; }
      if (this.data.supply_extra && !this.data.justificativa) { this.showSnack("Informe a justificativa da correção", "warning"); return; }
      this.createOrUpdate();
    },
    validateBlocked() {
      if (!this.$refs.blockedForm.validate()) { this.showSnack("Preencha os obrigatórios", "warning"); return; }
      this.createBlocked();
    },
    createOrUpdate() {
      this.loader = true; 
      // Se não houver fornecedor selecionado no form, tenta pegar do filtro ou do usuário
      if (!this.data.provider_id) {
        this.data.provider_id = this.userGroup == 1 ? this.filter.provider_id : this.$store.getters.getUserData.table_id;
      }
      if (!this.data.prefecture_id) {
        this.data.prefecture_id = this.filter.prefecture_id;
      }
      this.data.supply_status = "CONCLUIDO"; this.$store.dispatch("setLoader", true);
      this.$http.post("/supplies/single", this.data).then((r) => {
          this.showSnack(r.data.result ? r.data.result : r.data.warn, r.data.result ? "success" : "error");
          this.loader = false; this.$store.dispatch("setLoader", false);
          if (r.data.result) this.resetForm();
        }).catch((e) => { this.showSnack(e.message, "error"); this.loader = false; this.$store.dispatch("setLoader", false); });
    },
    createBlocked() {
      this.loader = true; 
      if (!this.dataBlocked.provider_id) {
        this.dataBlocked.provider_id = this.userGroup == 1 ? this.filter.provider_id : this.$store.getters.getUserData.table_id;
      }
      if (!this.dataBlocked.prefecture_id) {
        this.dataBlocked.prefecture_id = this.filter.prefecture_id;
      }
      this.dataBlocked.supply_status = "BLOQUEADO PARA DISTRIBUIÇÃO";
      this.dataBlocked.vehicle_id = null;
      this.dataBlocked.driver_id = null;
      this.dataBlocked.sec_name = "";
      this.dataBlocked.subsec_desc = "";
      this.dataBlocked.supply_extra = true;
      this.dataBlocked.nfe = false;
      this.dataBlocked.supply_extra_type = 1;
      this.$store.dispatch("setLoader", true);
      this.$http.post("/supplies/single", this.dataBlocked).then((r) => {
          this.showSnack(r.data.result ? r.data.result : r.data.warn, r.data.result ? "success" : "error");
          this.loader = false; this.$store.dispatch("setLoader", false);
          if (r.data.result) this.resetBlockedForm();
        }).catch((e) => { this.showSnack(e.message, "error"); this.loader = false; this.$store.dispatch("setLoader", false); });
    },
    resetForm() {
       this.data = { supply_id: null, supply_price_day: 0, datehour: this.getCurrentDateTimeBr(), supply_bidding_price: 0, supply_adjusted: false, supply_liters_stocked: 0, supply_billed: false, item_id: null, vehicle_id: null, driver_id: null, sec_id: null, subsec_id: null, sec_name: "", subsec_desc: "", justificativa: null, supply_status: "CONCLUIDO", supply_extra: false, supply_extra_type: 3, supply_km: "", supply_coupon: "" };
    },
    resetBlockedForm() {
       this.dataBlocked = { supply_id: null, supply_price_day: 0, datehour: this.getCurrentDateTimeBr(), supply_bidding_price: 0, supply_adjusted: false, supply_liters_stocked: 0, supply_billed: false, item_id: null, vehicle_id: null, driver_id: null, sec_id: null, subsec_id: null, sec_name: "", subsec_desc: "", justificativa: null, supply_status: "BLOQUEADO PARA DISTRIBUIÇÃO", supply_extra: true, supply_extra_type: 1, supply_km: "", supply_coupon: "" };
    },
    showSnack(m, c) { this.snack.show = true; this.snack.message = m; this.snack.color = c; },
    printBillingReport(item) {
      const vId = item.vehicle_id || 'null'; let url = '';
      if (item.sec_id) url = `/billings/report/single/${this.filter.startDate}/${this.filter.endDate}/${this.filter.prefecture_id}/${this.filter.provider_id}/${vId}/${item.sec_id}`;
      else url = `/billings/report/single/${this.filter.startDate}/${this.filter.endDate}/${this.filter.prefecture_id}/${this.filter.provider_id}/${vId}`;
      window.open(this.$router.resolve({ path: url }).href, '_blank');
    },
    printAllBillings() {
      if (!this.filter.prefecture_id || (this.userGroup == 1 && !this.filter.provider_id)) {
        this.showSnack("Filtre os dados antes de gerar o relatório geral", "warning");
        return;
      }
      const url = `/billings/report/all/${this.filter.startDate}/${this.filter.endDate}/${this.filter.prefecture_id}/${this.filter.provider_id}/null`;
      window.open(this.$router.resolve({ path: url }).href, '_blank');
    }
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');

.field-label {
  display: block;
  font-size: 9px;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 4px;
}

.field-input >>> .v-input__control .v-input__slot {
  background-color: #f8fafc !important;
  border-radius: 6px !important;
}

.field-input >>> .v-input__control .v-input__slot fieldset {
  border-color: #e2e8f0 !important;
  border-width: 1px !important;
}

.field-input >>> .v-input__control .v-input__slot:hover fieldset {
  border-color: #f37021 !important;
}

.field-input >>> input,
.field-input >>> .v-select__selection {
  font-size: 13px !important;
  color: #334155 !important;
}

.field-input-search >>> .v-input__control .v-input__slot {
  background: white !important;
  border-radius: 0.75rem !important;
  padding: 0 16px !important;
  height: 48px !important;
  font-size: 11px !important;
  font-weight: 700 !important;
  letter-spacing: 0.05em !important;
}

.premium-table ::v-deep table { 
  background-color: transparent !important; 
  border-collapse: separate; 
  border-spacing: 0; 
}

.premium-table ::v-deep th { 
  color: #94a3b8 !important; 
  font-weight: 900 !important; 
  text-transform: uppercase !important; 
  font-size: 10px !important; 
  letter-spacing: 0.1em !important;
  background-color: #fcfcfc !important;
  border-bottom: 1px solid #f1f5f9 !important;
  height: 64px !important;
  padding: 0 24px !important;
}

.premium-table ::v-deep td { 
  border-bottom: 1px solid #f8fafc !important;
  color: #334155 !important;
  padding: 12px 24px !important;
  font-size: 12px !important;
}

.premium-table ::v-deep tr:hover { background-color: #fcfcfc !important; }

.custom-snack >>> .v-snack__wrapper { border-radius: 1rem !important; }

@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
.animate-spin { animation: spin 1s linear infinite; }
</style>